import React, { useState, useEffect, useMemo } from 'react';
import { usePatchForm } from '../../providers/PatchFormProvider';
import { useAgentHeartBeatService } from '../../components/services/agentHeartBeatService';
import { AgentStatusIndicator } from '../../components/AgentStatus';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';

const DeviceAccordion = ({ heading, isExpanded, toggleExpanded }) => {
  const { patchFormData } = usePatchForm();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const { selectedTenantId } = useTenantSelection();

  const [heartbeatLoading, setHeartbeatLoading] = useState<boolean>(false);
  const [heartbeatData, setHeartbeatData] = useState<Record<string, any>>({});

  const isGroup = patchFormData.deviceSelection?.selectedType === 'Group';

  // Memoize displayed agents
  const displayedAgents = useMemo(() => {
    return isGroup
      ? patchFormData.deviceSelection?.agentsData?.filter((agent) =>
          patchFormData.deviceSelection.selectedAgentIds.includes(agent.tenantAgentId),
        )
      : patchFormData.deviceSelection?.osAgentsData?.filter((agent) =>
          patchFormData.deviceSelection.selectedDeviceIds.includes(agent.agentId),
        );
  }, [isGroup, patchFormData.deviceSelection]);

  // Memoize device IDs
  const deviceIds = useMemo(() => {
    return displayedAgents?.map((agent) => (isGroup ? agent.tenantAgentId : agent.agentId)) || [];
  }, [displayedAgents, isGroup]);

  // Fetch heartbeat data when expanded
  useEffect(() => {
    const fetchHeartbeat = async () => {
      if (!isExpanded || !deviceIds.length) return;

      setHeartbeatLoading(true);
      try {
        const data = await getAgentHeartbeatStatus(selectedTenantId as string, deviceIds);

        const statusMap: Record<string, { status: string; ipAddress: string; heartbeatStatusMessage: string }> = {};
        data.data.forEach((agent: any) => {
          const key = agent.tenantAgentId || agent.agentId;
          statusMap[key] = {
            status: agent.agentStatus || (agent.status ? 'Active' : 'Inactive'),
            ipAddress: agent.ipAddress || '-',
            heartbeatStatusMessage: agent.heartbeatStatusMessage || agent.heartBeatStatusMessage || agent.statusMessage || '',
          };
        });

        setHeartbeatData(statusMap);
      } catch (error) {
        console.error('Error fetching heartbeat:', error);
      } finally {
        setHeartbeatLoading(false);
      }
    };

    fetchHeartbeat();
  }, [isExpanded, selectedTenantId, deviceIds.join(',')]);

  return (
    <div className="bg-[#111] text-white p-4 rounded-md border border-white/20">
      {/* Upper Information Section */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-sm text-gray-400">Remediation Plan :</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-medium">{patchFormData.RemediationPlanData.PlanName}</span>
            {patchFormData.RemediationPlanData.IsEmergencyRemediationPlan && (
              <span className="bg-green-700 text-xs px-2 py-0.5 rounded-full">Emergency Remediation Plan</span>
            )}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-400">Criteria:</div>
          <div className="font-medium mt-1">{isGroup ? 'Group' : 'OS flavour'}</div>
          <div className="flex gap-2 mt-2">
            {patchFormData.targetDevice?.targetDeviceFilters?.additionalProp2?.map((item) => (
              <span key={item} className="bg-[#2a2a2a] text-xs px-3 py-1 rounded-full border border-white/10">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left table-fixed">
              <thead className="text-gray-400 uppercase border-b border-gray-600">
                <tr>
                  <th className="py-2 px-4">Device Name</th>
                  <th className="py-2 px-4">{isGroup ? 'Group Name' : 'OS'}</th>
                  <th className="py-2 px-4">IP Address</th>
                  <th className="py-2 px-4">Status</th>
                </tr>
              </thead>
            </table>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="min-w-full text-sm text-left table-fixed">
                <tbody>
                  {displayedAgents?.map((agent, index) => {
                    const key = isGroup ? agent.tenantAgentId : agent.agentId;
                    const heartbeat = heartbeatData[key];
                    const status = heartbeat?.status || 'Unknown';
                    const ipAddress = heartbeat?.ipAddress || '-';

                    return (
                      <tr key={index} className="border-b border-gray-700 hover:bg-[#2a2a2a]">
                        <td className="py-2 px-4">{agent.deviceName}</td>
                        <td className="py-2 px-4">{isGroup ? agent.groupName : `${agent.os} ${agent.osVersion}`}</td>
                        <td className="py-2 px-4">
                          {heartbeatLoading ? <div className="h-4 w-24 bg-gray-700 rounded-md animate-pulse"></div> : ipAddress}
                        </td>
                        <td className="py-2 px-4">
                          <AgentStatusIndicator
                            agentId={key}
                            isActive={status === 'Active'}
                            statusMessage={heartbeat?.heartbeatStatusMessage}
                            isLoading={heartbeatLoading}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceAccordion;
